import { useState, useEffect, useRef } from 'react';
import {
    Box, Heading, SimpleGrid, HStack, Select, Button, Badge,
    useToast, Icon, Flex, Spacer, Text, Checkbox, CheckboxGroup, VStack
} from '@chakra-ui/react';
import { FiRefreshCw, FiFilter, FiAlertCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import PickupStudentCard, { STATUS_CONFIG, pulse } from '../components/PickupStudentCard';

import { useWebSocket } from '../context/WebSocketContext';
import { useNotifications } from '../context/NotificationContext';
import { sortClasses } from '../utils/classUtils';

const PickupMonitorPage = () => {
    const { lastMessage, audioBlocked, enableAudio } = useWebSocket(); // Use WebSocket context
    const { lastDataUpdate } = useNotifications(); // 🆕 Signal
    const [students, setStudents] = useState([]);
    const lastProcessedMessageRef = useRef(null); // Track last processed message ID/Timestamp
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dataScope, setDataScope] = useState('PARTIAL'); // 'PARTIAL' | 'FULL'
    const [updatingIds, setUpdatingIds] = useState(new Set()); // 🆕 Track active updates
    const [completingIds, setCompletingIds] = useState(new Set()); // 離校淡出中：留原位、視覺標示「已離校」，不重排/不消失
    const fetchSeqRef = useRef(0); // 請求序號：過期回應直接丟棄，防閃爍
    const fetchStudentsRef = useRef(null); // 永遠指向最新 fetchStudents（修 interval 的 stale closure）

    // Filters
    const [selectedClass, setSelectedClass] = useState('ALL');
    // Default selected statuses
    // Default selected statuses (Optimized UX: Only show Urgent first)
    const [selectedStatuses, setSelectedStatuses] = useState(['PARENT_EN_ROUTE']);

    const toast = useToast();
    const intervalRef = useRef(null);

    // Status groups
    const ACTIVE_STATUSES = ['PARENT_EN_ROUTE', 'READY_FOR_PICKUP', 'HOMEWORK_PENDING', 'ARRIVED'];
    const INACTIVE_STATUSES = ['NOT_ARRIVED', 'PICKUP_COMPLETED'];

    // Initial Data Fetch with Phased Loading
    const fetchInitialData = async () => {
        try {
            setLoading(true);

            // Phase 1: Fetch Priority Data (Fast!)
            // Only fetch PARENT_EN_ROUTE
            const [priorityRes, classesRes] = await Promise.all([
                authAPI.getStudents({ limit: 1000, statuses: ['PARENT_EN_ROUTE'] }),
                authAPI.getClasses()
            ]);

            // Render Priority Data Immediately
            setStudents(sortStudents(priorityRes.data));
            setClasses(sortClasses(classesRes.data));
            setLoading(false); // Unblock UI!

            // Phase 2: Fetch Active Data (The rest of active students)
            // Excluding 'NOT_ARRIVED' and 'PICKUP_COMPLETED' to reduce payload
            const activeRes = await authAPI.getStudents({ limit: 1000, statuses: ACTIVE_STATUSES });
            setStudents(sortStudents(activeRes.data));

            // Note: We stay in 'PARTIAL' scope

        } catch (error) {
            console.error(error);
            toast({ title: "無法獲取資料", status: "error" });
            setLoading(false);
        }
    };

    // Polling Fetch（seq 防過期覆蓋 + functional 合併，永遠尊重「當下」的樂觀鎖）
    const fetchStudents = async () => {
        const seq = ++fetchSeqRef.current;
        try {
            // Respect current scope
            const params = { limit: 1000 };
            if (dataScope === 'PARTIAL') {
                params.statuses = ACTIVE_STATUSES;
            }
            const res = await authAPI.getStudents(params);
            // 若等待期間又發了更新的請求，這批是過期資料 → 丟棄（防閃爍/舊蓋新）
            if (seq !== fetchSeqRef.current) return;

            const locked = new Set([...updatingIds, ...completingIds]);
            setStudents(prev => {
                const serverIds = new Set(res.data.map(s => s.id));
                // 被鎖住的學生：保留本地（樂觀）狀態，不被 server 舊值蓋回
                const merged = res.data.map(serverStudent => {
                    if (locked.has(serverStudent.id)) {
                        const localStudent = prev.find(s => s.id === serverStudent.id);
                        return localStudent ? { ...serverStudent, status: localStudent.status } : serverStudent;
                    }
                    return serverStudent;
                });
                // 被鎖住但這批 server 沒回傳者（例：離校淡出中、active 查詢查不到）→ 保留本地卡片
                const preserved = prev.filter(s => locked.has(s.id) && !serverIds.has(s.id));
                return sortStudents([...merged, ...preserved]);
            });
        } catch (error) {
            console.error("Polling error", error);
        }
    };
    fetchStudentsRef.current = fetchStudents; // 每次 render 指向最新版，供 interval 呼叫（修 stale closure）

    // Lazy Load Trigger
    useEffect(() => {
        const needsFullData = selectedStatuses.some(s => INACTIVE_STATUSES.includes(s));
        if (needsFullData && dataScope === 'PARTIAL') {
            const loadFull = async () => {
                try {
                    setLoading(true); // Short spinner
                    const res = await authAPI.getStudents({ limit: 1000 });
                    setStudents(sortStudents(res.data));
                    setDataScope('FULL');
                } catch (e) {
                    console.error(e);
                } finally {
                    setLoading(false);
                }
            };
            loadFull();
        }
    }, [selectedStatuses, dataScope]);

    const sortStudents = (data) => {
        return data.sort((a, b) => {
            const pA = STATUS_CONFIG[a.status]?.priority ?? 99;
            const pB = STATUS_CONFIG[b.status]?.priority ?? 99;

            // Primary Sort: Status Priority
            if (pA !== pB) return pA - pB;

            // Secondary Sort: ETA (Only for PARENT_EN_ROUTE)
            // Sorter ETA = Higher Priority (Ascending)
            if (a.status === 'PARENT_EN_ROUTE') {
                const etaA = (a.eta_minutes !== undefined && a.eta_minutes !== null) ? a.eta_minutes : 9999;
                const etaB = (b.eta_minutes !== undefined && b.eta_minutes !== null) ? b.eta_minutes : 9999;
                return etaA - etaB;
            }

            return 0;
        });
    };

    const handleStatusUpdate = async (id, newStatus) => {
        const isLeaving = newStatus === 'PICKUP_COMPLETED';
        try {
            // 1. 上鎖，避免背景輪詢/訊號蓋掉
            setUpdatingIds(prev => new Set(prev).add(id));

            if (isLeaving) {
                // (b) 離校：先「不改狀態」→ 卡片留在原位、不重排；只標記 completing 讓它顯示「已離校」淡出
                setCompletingIds(prev => new Set(prev).add(id));
            } else {
                // 其他狀態：一般樂觀更新（立即改本地 UI）
                setStudents(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
            }

            // 2. API
            await authAPI.updateStudentStatus(id, newStatus);

            if (isLeaving) {
                // 3a. 淡出 2.2 秒後才真正落定為已離校 + 解鎖 → 卡片此時才自然移除（期間位置穩定、不誤點）
                setTimeout(() => {
                    setStudents(prev => prev.map(s => s.id === id ? { ...s, status: 'PICKUP_COMPLETED' } : s));
                    setCompletingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                    setUpdatingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                }, 2200);
            } else {
                // 3b. 一般狀態：2 秒後解鎖，確保輪詢/訊號已抓到新狀態
                setTimeout(() => {
                    setUpdatingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                }, 2000);
            }
        } catch (error) {
            toast({ title: "更新失敗", status: "error" });
            // 失敗：解鎖 + 清 completing + 重抓還原
            setUpdatingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            setCompletingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            fetchStudents();
        }
    };

    // WebSocket Listener (Legacy + Toast)
    useEffect(() => {
        if (lastMessage && lastMessage.type === 'broadcast') {
            // Prevent duplicate toasts for the same message signature
            const messageSignature = `${lastMessage.student_id}-${lastMessage.timestamp}`;
            if (lastProcessedMessageRef.current === messageSignature) {
                return;
            }
            lastProcessedMessageRef.current = messageSignature;

            // fetchStudents(); // Handled by lastDataUpdate now? 
            // Actually, let's keep it redundant or verify. 
            // NotificationContext also triggers lastDataUpdate on 'broadcast'.
            // So we can remove fetchStudents() here to avoid double fetch, 
            // OR keep it if we trust this local logic more. 
            // Let's rely on the signal for data consistency.

            // Optional: Show toast for specific events
            if (lastMessage.message) {
                toast({
                    title: "新通知",
                    description: lastMessage.message,
                    status: "info",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right"
                });
            }
        }
    }, [lastMessage]);

    // 🆕 Auto-Refresh on Data Signal
    useEffect(() => {
        console.log("Receive Data Signal, refreshing...", lastDataUpdate);
        fetchStudents();
        fetchPredictions(); // Also update predictions
    }, [lastDataUpdate]);

    useEffect(() => {
        fetchInitialData();
        // Speed up polling to 10s as requested（透過 ref 呼叫最新 fetchStudents，避免 stale closure 略過樂觀鎖）
        intervalRef.current = setInterval(() => fetchStudentsRef.current?.(), 10000);
        return () => clearInterval(intervalRef.current);
    }, []);

    // Filter Logic
    const filteredStudents = students.filter(student => {
        // Multi-class support: check if student.classes contains the selected class ID
        // Fallback to V1 class_id if classes array is empty or missing
        const studentClasses = student.classes || (student.class ? [student.class] : []);
        const classMatch = selectedClass === 'ALL' || studentClasses.some(c => c.id.toString() === selectedClass);

        // 離校淡出中：保持可見在原位（狀態尚未落定，排序不變）
        if (completingIds.has(student.id)) return classMatch;

        // Check if student status is in the selected statuses array
        const statusMatch = selectedStatuses.length > 0 && selectedStatuses.includes(student.status);
        return classMatch && statusMatch;
    });

    const urgentCount = students.filter(s => s.status === 'PARENT_EN_ROUTE').length;

    // Predictions
    const [showPredictions, setShowPredictions] = useState(false);
    const [predictions, setPredictions] = useState([]);

    const fetchPredictions = async () => {
        if (!showPredictions) return;
        try {
            const res = await authAPI.getRealtimePredictions();
            setPredictions(res.data);
        } catch (error) {
            console.error("Prediction fetch error", error);
        }
    };

    useEffect(() => {
        if (showPredictions) {
            fetchPredictions();
            const interval = setInterval(fetchPredictions, 600000); // 10 mins
            return () => clearInterval(interval);
        } else {
            setPredictions([]);
        }
    }, [showPredictions]);

    return (
        <Flex
            direction="column"
            h="100%"
            w="100%"
            mx={{ base: -2, md: -8 }} // Counteract AdminLayout padding: base=2 (8px), md=8 (32px)
            px={{ base: 2, md: 8 }}   // Maintain internal spacing
        >
            {/* Header & Controls - Fixed at Top */}
            <Box flexShrink={0}>
                {/* Top Line */}
                <Flex mb={2} justify="space-between" align="center" wrap="wrap" gap={2} pt={2}>
                    <Box>
                        <Heading size="md" color="brand.800">接送控制台</Heading>
                    </Box>

                    {/* Autoplay Blocker Banner */}
                    {audioBlocked && (
                        <Button
                            colorScheme="red"
                            variant="solid"
                            size="sm"
                            leftIcon={<Icon as={FiAlertCircle} />}
                            onClick={enableAudio}
                            animation={`${pulse} 2s infinite`}
                        >
                            點此開啟語音廣播
                        </Button>
                    )}

                    {/* Right: Buttons fixed top-right */}
                    <HStack spacing={4}>
                        {/* Urgency Indicator */}
                        {urgentCount > 0 && (
                            <HStack bg="red.100" color="red.700" px={3} py={2} borderRadius="md" animation={`${pulse} 2s infinite`}>
                                <Icon as={FiAlertCircle} />
                                <Text fontWeight="bold">{urgentCount} 位家長路途中</Text>
                            </HStack>
                        )}
                        <Button leftIcon={<FiRefreshCw />} onClick={() => { fetchStudents(); fetchPredictions(); }} colorScheme="blue">
                            重整
                        </Button>
                    </HStack>
                </Flex>

                {/* Filters Section - Compact */}
                <Box bg="white" p={2} borderRadius="md" shadow="sm" border="1px solid" borderColor="gray.100" w="100%" mb={2}>
                    <Flex direction={{ base: "column", md: "row" }} gap={2} align={{ md: "center" }}>
                        {/* Class Filter */}
                        <HStack spacing={2}>
                            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" fontWeight="bold">班級:</Text>
                            <Select
                                variant="filled"
                                size={{ base: "xs", md: "sm" }}
                                w={{ base: "140px", md: "200px" }}
                                borderRadius="md"
                                value={selectedClass}
                                onChange={(e) => setSelectedClass(e.target.value)}
                            >
                                <option value="ALL">精簡模式</option>
                                {classes.map(cls => (
                                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                                ))}
                            </Select>
                        </HStack>

                        <Box borderLeft={{ md: "1px solid" }} borderColor="gray.200" h={{ md: "20px" }} mx={{ md: 2 }} />

                        {/* Status Filter - Scrollable on mobile if too many */}
                        <Box overflowX="auto" py={1} flex="1">
                            <CheckboxGroup colorScheme="brand" value={selectedStatuses} onChange={setSelectedStatuses}>
                                <HStack spacing={{ base: 3, md: 5 }}>
                                    <Checkbox value="PARENT_EN_ROUTE" size={{ base: "sm", md: "md" }}><Badge colorScheme="red" variant="subtle" fontSize={{ base: "9px", md: "xs" }}>路途中</Badge></Checkbox>
                                    <Checkbox value="READY_FOR_PICKUP" size={{ base: "sm", md: "md" }}><Badge colorScheme="green" variant="subtle" fontSize={{ base: "9px", md: "xs" }}>可接</Badge></Checkbox>
                                    <Checkbox value="HOMEWORK_PENDING" size={{ base: "sm", md: "md" }}><Badge colorScheme="orange" variant="subtle" fontSize={{ base: "9px", md: "xs" }}>留班</Badge></Checkbox>
                                    <Checkbox value="ARRIVED" size={{ base: "sm", md: "md" }}><Badge colorScheme="blue" variant="subtle" fontSize={{ base: "9px", md: "xs" }}>已進</Badge></Checkbox>

                                    <Box borderLeft="1px solid" borderColor="gray.300" h="16px" />
                                    <Checkbox
                                        isChecked={showPredictions}
                                        onChange={(e) => setShowPredictions(e.target.checked)}
                                        colorScheme="purple"
                                        size={{ base: "sm", md: "md" }}
                                    >
                                        <Text fontSize={{ base: "xs", md: "sm" }}>🔮預測</Text>
                                    </Checkbox>
                                </HStack>
                            </CheckboxGroup>
                        </Box>
                    </Flex>
                </Box>
            </Box>

            {/* Content Grid - Scrollable Area */}
            <Box flex="1" overflowY="auto" pb={4} pr={1} css={{
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
            }}>
                {(filteredStudents.length > 0 || (showPredictions && predictions.length > 0)) ? (
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={2}>
                        {/* 1. Real Active Students */}
                        {filteredStudents.map(student => (
                            <PickupStudentCard
                                key={student.id}
                                student={student}
                                onUpdate={handleStatusUpdate}
                                isUpdating={updatingIds.has(student.id)}
                                isCompleting={completingIds.has(student.id)}
                            />
                        ))}

                        {/* 2. Ghost Cards (Predictions) */}
                        {showPredictions && predictions.map(pred => {
                            // Filter out if student is already shown in the active list (Avoid duplicates)
                            if (filteredStudents.some(s => s.id === pred.student_id)) return null;

                            return (
                                <Box
                                    key={`pred-${pred.student_id}`}
                                    p={4}
                                    borderWidth="2px"
                                    borderStyle="dashed"
                                    borderColor="purple.300"
                                    borderRadius="lg"
                                    bg="purple.50"
                                    opacity={0.6}
                                    position="relative"
                                >
                                    <VStack align="start" spacing={2}>
                                        <HStack justify="space-between" w="100%">
                                            <Heading size="md" color="purple.800">{pred.student_name}</Heading>
                                            <Icon as={FiAlertCircle} color="purple.500" />
                                        </HStack>
                                        <Badge colorScheme="purple">智慧預測</Badge>
                                        <Text fontSize="sm" fontWeight="bold" color="purple.700">
                                            家長預計 {pred.predicted_time} 到達
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            信心指數: {pred.confidence}
                                        </Text>
                                    </VStack>
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                ) : (
                    /* Full Width Empty State */
                    // 卡片面 white：行政外殼就是 neutral.50（=gray.50），原本填色與底色同階，
                    // 虛線框裡外看起來一樣，空狀態讀不出「這是一塊區域」
                    <Box w="100%" textAlign="center" py={20} color="gray.400" bg="white" borderRadius="xl" border="2px dashed" borderColor="gray.200">
                        <VStack spacing={4}>
                            <Icon as={FiFilter} boxSize={10} />
                            <Text fontSize="lg" fontWeight="bold">
                                {selectedStatuses.length === 0 ? "尚未勾選任何狀態" : "沒有符合條件的學生"}
                            </Text>
                            <Text fontSize="sm">請調整上方的篩選條件</Text>
                        </VStack>
                    </Box>
                )}
            </Box>
        </Flex>
    );
};

export default PickupMonitorPage;
